import React, { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import Sidebar from "./Components/Core/Sidebar";
import TopBar from "./Components/Core/TopBar";
import { 
  LazyInfo, 
  LazyPatients, 
  LazyPatientDetail, 
  LazySedationWorkflow, 
  LazyIntraSedation, 
  LazyPostSedation, 
  LazySedationList,
  LazyImportReport,
  LazyTipsAndTutorials
} from "./shared/LazyComponents";
import networkService from "./hooks/services/networkService";
import { CurrentPatientProvider, useCurrentPatient } from "./hooks/providers/CurrentPatientProvider";
import useBackgroundSync from "./hooks/useBackgroundSync";
import { useUpcomingPrefetch } from "./hooks/useUpcomingPrefetch";
import ProductionSyncMonitor from "./Components/Debug/ProductionSyncMonitor";

const AuthenticatedApp: React.FC = () => {


  // Enhanced background sync for production reliability (ONLY sync service active)
  useBackgroundSync({
    intervalMs: 30000, // 30 seconds
    enabled: true,
    enableLogging: true,
    maxAttempts: 10,
    batchSize: 25,
    autoStartOnAuth: true
  });

  useUpcomingPrefetch();

  // Production monitoring (can be conditionally enabled)
  const showSyncMonitor = process.env.REACT_APP_SHOW_SYNC_MONITOR === 'true';


  useEffect(() => {
    networkService.startMonitoring();
    return () => networkService.stopMonitoring();
  }, []);

  return (
    <CurrentPatientProvider>
      <AppContent />
      {showSyncMonitor && <ProductionSyncMonitor compact={true} />}
    </CurrentPatientProvider>
  );
};

const AppContent: React.FC = () => {
  const { currentPatient } = useCurrentPatient();

  return (
    <div className="app-container">
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', minWidth: 0 }}>
        <TopBar 
          currentPatient={currentPatient ? {
            id: currentPatient.id,
            name: currentPatient.name
          } : null} 
        />
        <div className="main-content" style={{ flex: 1, overflow: 'auto', width: '100%', minWidth: 0 }}>
          <Routes>
            <Route path="/" element={<LazyInfo />} />
            <Route path="/patients" element={<LazyPatients />} />
            {/* Redirect all patient creation/editing to SedationWorkflow */}
            <Route
              path="/patients/new"
              element={<LazySedationWorkflow mode="create" />}
            />
            <Route
              path="/patients/edit/:id"
              element={<LazySedationWorkflow mode="edit" />}
            />
            <Route path="/patients/:id" element={<LazyPatientDetail />} />
            
            {/* Pre-sedation workflow - new patients start here */}
            <Route
              path="/pre-sedation/:patientId"
              element={<LazySedationWorkflow mode="edit" />}
            />
            
            {/* Central Sedation Workflow Routes */}
            <Route
              path="/sedation-workflow"
              element={<LazySedationWorkflow mode="create" />}
            />
            <Route
              path="/sedation-workflow/:patientId"
              element={<LazySedationWorkflow mode="edit" />}
            />
            <Route
              path="/sedation-workflow/:patientId/:sedationId"
              element={<LazySedationWorkflow mode="edit" />}
            />
            <Route
              path="/sedation-workflow/:patientId/:sedationId/edit"
              element={<LazySedationWorkflow mode="edit" />}
            />
            
            {/* Legacy routes - redirect to sedation workflow */}
            <Route
              path="/patients/:patientId/sedations/new"
              element={<LazySedationWorkflow mode="create" />}
            />
            <Route
              path="/patients/:patientId/sedations/edit/:sedationId"
              element={<LazySedationWorkflow mode="edit" />}
            />
            <Route
              path="/sedations/new"
              element={<LazySedationWorkflow mode="create" />}
            />
            <Route
              path="/sedations/edit/:patientId"
              element={<LazySedationWorkflow mode="edit" />}
            />
            
            {/* Specific sedation phases - accessible from SedationWorkflow */}
            <Route
              path="/intra-sedation/:patientId"
              element={<LazyIntraSedation />}
            />
            <Route
              path="/post-sedation/:patientId"
              element={<LazyPostSedation />}
            />
            <Route path="/sedation-list" element={<LazySedationList />} />
            <Route path="/tips-and-tutorials" element={<LazyTipsAndTutorials />} />
            {/* Admin-only route — no sidebar entry, accessible via hidden debug modal only */}
            <Route path="/import-report" element={<LazyImportReport />} />
            {/* Fallback to landing to prevent blank screens */}
            <Route path="*" element={<LazyInfo />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default AuthenticatedApp;
